import React from 'react';
import Sparkline from './Sparkline';
import './charts.css';

// Premium-fintech KPI tile: big number + delta + optional sparkline.
//
// Props:
//   label:      string (e.g. "Orders (24h)")
//   value:      string | number — the big number, pre-formatted
//   suffix:     string — appended in lighter weight (e.g. "USDT", "BNB")
//   delta:      { pct?: number, abs?: number | string, label?: string }
//                  pct/abs: positive = up (green), negative = down (red)
//                  label: optional override ("vs prior 24h" etc)
//   series:     number[] — sparkline data (optional)
//   accent:     'gold' | 'green' | 'red' | 'amber' (default 'gold')
//   status:     'ok' | 'warn' | 'critical' (overrides accent for status tiles)
//   icon:       react node — optional inline icon

const STATUS_ACCENTS = { ok: 'gold', warn: 'amber', critical: 'red' };
const ACCENT_COLORS = {
  gold:  '#00e0ff',
  green: '#34d399',
  red:   '#f87171',
  amber: '#fbbf24',
};

const KpiTile = ({
  label,
  value,
  suffix,
  delta,
  series,
  accent = 'gold',
  status,
  icon,
  onClick,
  hint,
}) => {
  const clickable = typeof onClick === 'function';
  const effectiveAccent = status ? STATUS_ACCENTS[status] || accent : accent;
  const accentColor = ACCENT_COLORS[effectiveAccent] || ACCENT_COLORS.gold;

  let deltaPct = null;
  let deltaIsUp = null;
  let deltaText = null;
  if (delta) {
    if (typeof delta.pct === 'number') {
      deltaPct = delta.pct;
      deltaIsUp = deltaPct >= 0;
      deltaText = `${deltaIsUp ? '▲' : '▼'} ${Math.abs(deltaPct).toFixed(1)}%`;
    } else if (delta.abs != null) {
      const n = Number(delta.abs);
      deltaIsUp = n >= 0;
      deltaText = `${deltaIsUp ? '+' : ''}${delta.abs}`;
    }
  }
  const deltaCls = deltaIsUp === null ? '' : deltaIsUp ? 'kpi-tile-delta--up' : 'kpi-tile-delta--down';

  return (
    <div
      className={`kpi-tile kpi-tile--${effectiveAccent} ${clickable ? 'kpi-tile--clickable' : ''}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      title={clickable ? (hint || `View ${label}`) : undefined}
    >
      <div className="kpi-tile-head">
        <span className="kpi-tile-label">{label}</span>
        {(icon || clickable) && <span className="kpi-tile-icon" aria-hidden="true">{icon || '↗'}</span>}
      </div>
      <div className="kpi-tile-value-row">
        <span className="kpi-tile-value">{value}</span>
        {suffix && <span className="kpi-tile-suffix">{suffix}</span>}
      </div>
      <div className="kpi-tile-meta-row">
        {deltaText && (
          <span className={`kpi-tile-delta ${deltaCls}`}>
            {deltaText}
            {delta?.label && <span className="kpi-tile-delta-sub"> {delta.label}</span>}
          </span>
        )}
        {Array.isArray(series) && series.length > 1 && (
          <Sparkline data={series} width={92} height={26} color={accentColor} />
        )}
      </div>
    </div>
  );
};

export default KpiTile;
